import React, { createContext, useContext, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import "@/App.css";

// Pages
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import BookingPage from "./pages/BookingPage";
import GalleryPage from "./pages/GalleryPage";
import ContactPage from "./pages/ContactPage";
import AdminPage from "./pages/AdminPage";
import LoginPage from "./pages/LoginPage";
import ReviewPage from "./pages/ReviewPage";
import SalesCRMPage from "./pages/SalesCRMPage";
import ClientLoginPage from "./pages/ClientLoginPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import QuoteBuilderPage from "./pages/QuoteBuilderPage";
import ServiceLandingPage from "./pages/ServiceLandingPage";
import QuoteViewPage from "./pages/QuoteViewPage";
import EmployeeLoginPage from "./pages/EmployeeLoginPage";
import EmployeePortalPage from "./pages/EmployeePortalPage";
import FinancingPage from "./pages/FinancingPage";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import GhostAssistant from "./components/GhostAssistant";
import MobileStickyCTA from "./components/MobileStickyCTA";
import ExitIntentPopup from "./components/ExitIntentPopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Analytics tracking utility
window._track = (event, data = {}) => {
  fetch(`${API}/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event, data }),
    credentials: "include"
  }).catch(() => {});
};

// Emergent Badge Removal - Kill Switch
const removeEmergentBadge = () => {
  const selectors = [
    '[class*="emergent"]',
    '[id*="emergent"]',
    '[class*="Emergent"]',
    '[id*="Emergent"]',
    'iframe[src*="emergent"]',
    '[data-emergent]',
    '.made-with-emergent',
    '#made-with-emergent',
    'a[href*="emergent"]'
  ];
  
  selectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => {
      el.style.display = 'none';
      el.style.visibility = 'hidden';
      el.style.opacity = '0';
      el.remove();
    });
  });
  
  // Remove any suspicious fixed bottom-right elements
  document.querySelectorAll('body > div').forEach(el => {
    const style = window.getComputedStyle(el);
    if (style.position === 'fixed' && 
        style.bottom !== 'auto' && 
        style.right !== 'auto' &&
        !el.hasAttribute('data-testid')) {
      const text = el.textContent?.toLowerCase() || '';
      if (text.includes('emergent') || text.includes('made with')) {
        el.remove();
      }
    }
  });
};

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    
    // Emergent badge removal - run on mount and interval
    removeEmergentBadge();
    const badgeKiller = setInterval(removeEmergentBadge, 1000);
    
    return () => clearInterval(badgeKiller);
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0C10]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  return user ? children : null;
};

// Layout wrapper
const Layout = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdminPage = location.pathname.startsWith("/admin") || location.pathname === "/login";

  return (
    <div className="min-h-screen flex flex-col bg-[#0B0C10]">
      <Navbar />
      <main className="flex-grow mobile-cta-spacing">
        {children}
      </main>
      {!isAdminPage && <Footer />}
      {!isAdminPage && <GhostAssistant />}
      {!isAdminPage && <MobileStickyCTA />}
      {!isAdminPage && <ExitIntentPopup />}
      {/* Admin Failsafe Access Button */}
      {user?.role === "admin" && !location.pathname.startsWith("/admin") && (
        <Link
          to="/admin"
          data-testid="admin-failsafe-btn"
          className="fixed top-20 right-4 z-50 flex items-center space-x-1.5 px-3 py-1.5 bg-[#15161C] border border-[#FF6B35]/50 rounded-lg text-[#FF6B35] text-xs font-semibold hover:bg-[#FF6B35] hover:text-white transition-all shadow-lg"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <span>Admin</span>
        </Link>
      )}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/financing" element={<FinancingPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/s/:slug" element={<ServiceLandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/client-login" element={<ClientLoginPage />} />
            <Route path="/review/:reviewId" element={<ReviewPage />} />
            <Route path="/quote/:quoteId" element={<QuoteViewPage />} />
            <Route path="/employee/login" element={<EmployeeLoginPage />} />
            <Route path="/employee" element={<EmployeePortalPage />} />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            } />
            <Route path="/crm" element={
              <ProtectedRoute>
                <SalesCRMPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/quotes/new" element={
              <ProtectedRoute>
                <QuoteBuilderPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/quotes/:quoteId" element={
              <ProtectedRoute>
                <QuoteBuilderPage />
              </ProtectedRoute>
            } />
            <Route path="/client-portal" element={
              <ProtectedRoute>
                <ClientPortalPage />
              </ProtectedRoute>
            } />
          </Routes>
        </Layout>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API };
